// Kết quả sau khi thu tiền thẻ: số tiền, tiền thối, còn nợ, đợt kế tiếp; in phiếu thu K80 / A4, gửi Zalo, in hóa đơn bán thẻ
import { Modal, message } from 'antd';
import { CheckCircle2, FileText, Printer, ReceiptText, Send } from 'lucide-react';
import usePosStore from '../POS/store';
import { currentUserName, formatPhone, printReceipt } from '../POS/utils';
import { paymentAllocation } from './logic';
import useTreatmentStore from './store';
import { printCardReceipt, receiptNo } from './print';
import { fmtDate, money } from './ui';

export interface PaidResult {
  cardId: string;
  /** Lần thu vừa ghi (không có khi bán thẻ chưa thu đồng nào) */
  paymentId: string | null;
  change: number;
  tendered: number;
  /** Đơn POS bán thẻ — để in hóa đơn bán hàng */
  orderId?: string;
  title?: string;
}

export default function PaidModal({ result, onClose }: { result: PaidResult | null; onClose: () => void }) {
  const card = useTreatmentStore(s => s.cards.find(c => c.id === result?.cardId));
  const logPrint = useTreatmentStore(s => s.logPrint);
  const order = usePosStore(s => s.orders.find(o => o.id === result?.orderId));
  const alloc = card && result?.paymentId ? paymentAllocation(card, result.paymentId) : null;
  const open = !!result && !!card;

  const print = (size: 'K80' | 'A4') => {
    if (card && result?.paymentId) printCardReceipt(card, result.paymentId, size, { change: result.change, tendered: result.tendered > (alloc?.payment.amount || 0) ? result.tendered : undefined });
  };
  const sendZalo = () => {
    if (!card || !alloc) return;
    if (!card.customerPhone) { message.warning('Khách không có số điện thoại'); return; }
    logPrint(card.id, currentUserName(), `Gửi phiếu thu ${receiptNo(alloc.payment)} qua Zalo tới ${formatPhone(card.customerPhone)}`);
    message.success(`Đã gửi phiếu thu qua Zalo tới ${formatPhone(card.customerPhone)} (mô phỏng)`);
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} centered width={500} destroyOnHidden>
      {card && result && (
        <div className="py-2 text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-green-600" />
          <h2 className="mt-2 text-xl font-bold text-slate-800">{result.title || 'Đã thu tiền thẻ'}</h2>
          <p className="mt-1 text-sm text-slate-500">{card.customerName} · {card.packageName} · <span className="font-mono">{card.id}</span></p>
          {alloc ? (
            <div className="mt-4 grid grid-cols-2 gap-3 text-left">
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-slate-500">Số tiền thu</div>
                <div className="text-xl font-bold text-slate-800">{money(alloc.payment.amount)}đ</div>
                <div className="text-xs text-slate-500">{alloc.items.map(i => `Đợt ${i.no}`).join(', ')} · phiếu <span className="font-mono">{receiptNo(alloc.payment)}</span></div>
              </div>
              <div className={`rounded-xl p-3 ${alloc.debtAfter ? 'bg-amber-50' : 'bg-green-50'}`}>
                <div className="text-xs text-slate-500">{alloc.debtAfter ? 'Còn nợ' : 'Tình trạng'}</div>
                <div className={`text-xl font-bold ${alloc.debtAfter ? 'text-amber-700' : 'text-green-700'}`}>{alloc.debtAfter ? `${money(alloc.debtAfter)}đ` : 'Đã đủ'}</div>
                {alloc.next && <div className="text-xs text-slate-500">Đợt kế {fmtDate(alloc.next.dueDate)} · {money(alloc.next.left)}đ</div>}
              </div>
              {result.change > 0 && (
                <div className="col-span-2 rounded-xl bg-[#ecf6f9] p-3 text-center">
                  <div className="text-xs text-slate-500">Tiền thối cho khách</div>
                  <div className="text-3xl font-extrabold text-[#2c6b7f]">{money(result.change)}đ</div>
                </div>
              )}
            </div>
          ) : <p className="mt-3 text-sm text-slate-500">Chưa thu tiền – toàn bộ giá thẻ nằm trong lịch thanh toán.</p>}
          <div className="mt-5 grid grid-cols-2 gap-2">
            {alloc && <>
              <button onClick={() => print('K80')} className="flex h-11 items-center justify-center gap-2 rounded-lg bg-[#357f96] text-sm font-semibold text-white hover:bg-[#2c6b7f]"><Printer className="h-4 w-4" /> In phiếu thu K80</button>
              <button onClick={() => print('A4')} className="flex h-11 items-center justify-center gap-2 rounded-lg border border-slate-300 text-sm font-semibold hover:border-[#3e91ab]"><FileText className="h-4 w-4" /> In phiếu thu A4</button>
              <button onClick={sendZalo} className="flex h-11 items-center justify-center gap-2 rounded-lg border border-slate-300 text-sm font-semibold hover:border-[#3e91ab]"><Send className="h-4 w-4" /> Gửi Zalo</button>
            </>}
            {order && (
              <button onClick={() => printReceipt(order, 'K80')} className="flex h-11 items-center justify-center gap-2 rounded-lg border border-slate-300 text-sm font-semibold hover:border-[#3e91ab]"><ReceiptText className="h-4 w-4" /> Hóa đơn bán thẻ</button>
            )}
            <button onClick={onClose} className={`h-11 rounded-lg border border-slate-300 text-sm font-semibold text-slate-600 hover:bg-slate-50 ${alloc && !order ? '' : 'col-span-2'}`}>Đóng</button>
          </div>
          <p className="mt-3 text-xs text-slate-400">Muốn lưu PDF: chọn “Lưu dưới dạng PDF” trong hộp thoại in.</p>
        </div>
      )}
    </Modal>
  );
}
